<template>
	<div v-if="posts.length && frameWidth && latestTimestamp > startTimestamp" class="post-timeline" @mousemove.stop @touchmove.stop :style="{ width: frameWidth + 'px' }" @mouseleave="clearHover">
		<div v-for="entry in entries" :key="entry.post.id" class="post-marker" :class="{ 'post-marker-text': entry.posts.length === 1 && !entry.media && !['Bewässerung', 'Niederschlag', 'Fehler'].includes(entry.post.category) }" :style="{ left: entry.x + 'px', zIndex: activeId === entry.post.id ? 1 : 0 }" @mouseenter="hoverPost(entry.post)" @mouseleave="leavePost" @click="$emit('select', entry.post)">
			<span v-if="entry.posts.length > 1" class="post-count">{{ entry.posts.length }}</span>
			<Icon v-else-if="entry.post.category === 'Bewässerung'" type="post-bewaessert" :size="18" />
			<Icon v-else-if="entry.post.category === 'Niederschlag'" type="post-niederschlag" :size="16" />
			<Icon v-else-if="entry.post.category === 'Fehler'" type="post-fehler" :size="18" />
			<Icon v-else-if="entry.media" :type="entry.media.type === 'video' ? 'post-video' : 'post-imag'" :size="12" :opacity="0.95" />
			<Icon v-else type="post-text" :size="18" />
		</div>
		<Transition name="post-preview-fade">
		<div v-if="active" ref="preview" class="post-preview" :class="{ below: previewBelow }" @mouseleave="leavePost" :style="{ left: Math.max(0, Math.min(active.x - previewWidth / 2, frameWidth - previewWidth)) + 'px', width: previewWidth + 'px' }">
			<div v-for="entry in activePosts" :key="entry.post.id" class="post-preview-item" :style="{ background: getPostCategoryColor(entry.post.category) }" @mouseenter="$emit('hover', Number(entry.post.timestamp))" @click="$emit('select', entry.post)">
				<div class="post-preview-header">
					<span class="post-preview-date">{{ formatDate(entry.post.timestamp) }}</span>
					<span class="post-item-separator"></span>
					<span class="post-preview-author">{{ entry.post.authorName || 'Unbekannt' }}</span>
				</div>
				<p v-if="entry.post.content">{{ entry.post.content }}</p>
				<div v-if="entry.media.length" class="post-preview-gallery">
					<img v-for="media in entry.media" :key="media.id" :src="media.variants.thumbnail.url" alt="" @load="updatePreviewPlacement">
				</div>
			</div>
		</div>
		</Transition>
	</div>
</template>

<script>
import { displayutil } from '@/displayutil.js';
import { state } from '@/state.js';
import Icon from '@/ui/Icon.vue';

export default {
	name: 'PostTimeline',
	components: { Icon },
	emits: ['select', 'hover'],
	props: {
		device: { type: Object, required: true },
		startTimestamp: { type: Number, required: true },
		latestTimestamp: { type: Number, required: true },
		chartWidth: { type: Number, required: true },
		frameWidth: { type: Number, required: true },
		scrollLeft: { type: Number, default: 0 },
	},
	data: () => ({ activeId: null, previewBelow: false }),
	computed: {
		previewWidth() { return Math.min(200, this.frameWidth); },
		posts() {
			return state.posts.filter(post => post?.deviceId === this.device.id);
		},
		entries() {
			const duration = this.latestTimestamp - this.startTimestamp;
			if (duration <= 0) return [];
			const entries = [];
			for (const post of [...this.posts].sort((a, b) => Number(a.timestamp) - Number(b.timestamp))) {
				const x = (Number(post.timestamp) - this.startTimestamp) / duration * this.chartWidth - this.scrollLeft;
				if (!Number.isFinite(x) || x < 0 || x > this.frameWidth) continue;
				const previous = entries[entries.length - 1];
				if (previous && x - previous.x < 18) previous.posts.push(post);
				else entries.push({ post, media: this.postMedia(post), x, posts: [post] });
			}
			return entries;
		},
		activePosts() {
			if (!this.active) return [];
			const date = this.formatDate(this.active.post.timestamp);
			return this.posts.filter(post => this.formatDate(post.timestamp) === date || this.active.posts.includes(post))
				.sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
				.map(post => ({ post, media: (post.mediaIds || []).map(id => state.media[id]).filter(media => media?.variants?.thumbnail?.url) }));
		},
		active() { return this.entries.find(entry => entry.post.id === this.activeId); },
	},
	watch: {
		activePosts() {
			this.$nextTick(this.updatePreviewPlacement);
		},
	},
	beforeUnmount() {
		if (this.activeId !== null) this.clearHover();
	},
	methods: {
		getPostCategoryColor: category => displayutil.getPostCategoryColor(category),
		updatePreviewPlacement() {
			const preview = this.$refs.preview;
			if (!this.active || !preview) return;
			const markerTop = this.$el.getBoundingClientRect().top - 13;
			this.previewBelow = preview.offsetHeight + 8 > markerTop;
		},
		postMedia(post) {
			return (post.mediaIds || []).map(id => state.media[id]).find(Boolean);
		},
		hoverPost(post) {
			this.activeId = post.id;
			this.$emit('hover', Number(post.timestamp));
		},
		leavePost(event) {
			const next = event.relatedTarget;
			if (next instanceof Element && this.$el.contains(next) && next.closest('.post-marker, .post-preview')) return;
			this.clearHover();
		},
		clearHover() {
			this.activeId = null;
			this.$emit('hover', null);
		},
		formatDate(timestamp) {
			return new Date(Number(timestamp)).toLocaleDateString('de-DE');
		},
	},
};
</script>
